import { SQUAD_COLORS } from './templateConstants';

/**
 * Value labels on the rolling performance bars (SQUAD 30.10.2026).
 *
 * A label sits inside its bar when it fits — in height and in width — and just beyond the
 * bar end when it does not: -1,07 % is a sliver a label would only half cover. A value of
 * exactly 0 gets its label just above the zero line; a missing period gets none (the chart
 * formatter leaves it empty). ApexCharts places every label of a
 * chart the same way, so the labels are laid out after drawing (layoutSquadRollingDataLabels)
 * and the export picks up that layout: captureSquadChart serialises the live SVG.
 *
 * Colours, for print contrast:
 * - inside the brutto bar (series 0, the fund colour: navy, blue, green …): white;
 * - inside the netto bar (series 1, gold): navy;
 * - outside a bar, on white: navy.
 * By series, not by measuring the bar colour: SQUAD's light blue takes white as well.
 */

export const SQUAD_ROLLING_LABEL_COLORS = {
  insideFundColour: SQUAD_COLORS.white,
  insideGold: SQUAD_COLORS.text,
  outside: SQUAD_COLORS.text,
} as const;

/** Inside colour per series, in series order — ApexCharts' `dataLabels.style.colors`. */
export const SQUAD_ROLLING_LABEL_INSIDE_COLORS = [
  SQUAD_ROLLING_LABEL_COLORS.insideFundColour,
  SQUAD_ROLLING_LABEL_COLORS.insideGold,
];

/**
 * Label ink relative to the font size: digits and "%" reach 0.72 em above the baseline, the
 * comma 0.15 em below it. Measured for Helvetica/Arial; Segoe UI Bold, the chart font since
 * 30.10.2026, stays within (digits 0.700, "%" 0.716, comma 0.124 em).
 */
const INK_ABOVE_BASELINE_EM = 0.72;
const INK_BELOW_BASELINE_EM = 0.15;

/** Room between label ink and the bar end (px), inside and outside. */
export const SQUAD_ROLLING_LABEL_GAP_PX = 2;

export type SquadRollingLabelInput = {
  value: number;
  /** Bar edges in plot coordinates (px, y grows downward). A zero bar has top === bottom. */
  barTop: number;
  barBottom: number;
  /** Plot area height (px) — labels stay within 0 … gridHeight. */
  gridHeight: number;
  fontSizePx: number;
  seriesIndex: number;
  /** Label and bar width (px). Without them only the height decides. */
  labelWidthPx?: number;
  barWidthPx?: number;
};

export type SquadRollingLabelPlacement = {
  inside: boolean;
  /** SVG text `y` — the baseline. */
  baseline: number;
  color: string;
};

export function placeSquadRollingLabel({
  value,
  barTop,
  barBottom,
  gridHeight,
  fontSizePx,
  seriesIndex,
  labelWidthPx,
  barWidthPx,
}: SquadRollingLabelInput): SquadRollingLabelPlacement {
  const above = INK_ABOVE_BASELINE_EM * fontSizePx;
  const below = INK_BELOW_BASELINE_EM * fontSizePx;
  const gap = SQUAD_ROLLING_LABEL_GAP_PX;
  const barHeight = Math.abs(barBottom - barTop);
  // Narrow bars (more periods) would cut a white label at the bar edge.
  const fitsAcross =
    labelWidthPx === undefined || barWidthPx === undefined || labelWidthPx + 2 * gap <= barWidthPx;

  if (fitsAcross && barHeight >= above + below + 2 * gap) {
    // Digits centred in the bar; the comma tail may use part of the lower gap.
    return {
      inside: true,
      baseline: (barTop + barBottom) / 2 + above / 2,
      color:
        seriesIndex === 0
          ? SQUAD_ROLLING_LABEL_COLORS.insideFundColour
          : SQUAD_ROLLING_LABEL_COLORS.insideGold,
    };
  }

  // Beyond the bar end: above a positive bar, below a negative one.
  const overTop = barTop - gap - below;
  const underBottom = barBottom + gap + above;
  const fitsOverTop = overTop - above >= 0;
  const fitsUnderBottom = underBottom + below <= gridHeight;
  let baseline = value < 0 ? underBottom : overTop;
  // No room on that side of the plot: the other end of a sliver is the zero line, so
  // the label still does not touch the bar there.
  if (value < 0 && !fitsUnderBottom && fitsOverTop) baseline = overTop;
  if (value >= 0 && !fitsOverTop && fitsUnderBottom) baseline = underBottom;
  // Last resort, never outside the plot area.
  baseline = Math.min(Math.max(baseline, above), gridHeight - below);

  return { inside: false, baseline, color: SQUAD_ROLLING_LABEL_COLORS.outside };
}

export type SquadRollingOutsideLabel = {
  /** Label centre and width (px). */
  x: number;
  width: number;
  value: number;
  baseline: number;
};

/**
 * Outside labels of one period, in series order. From about 7 periods a bar is narrower
 * than its label, so brutto and netto labels at the same height run into each other
 * ("-17,62%-21,54%"). Each later label that would touch an earlier one moves one line
 * further out, away from the zero line; it stays within the plot.
 */
export function staggerSquadRollingLabels(
  labels: SquadRollingOutsideLabel[],
  fontSizePx: number,
  gridHeight: number,
): number[] {
  const above = INK_ABOVE_BASELINE_EM * fontSizePx;
  const below = INK_BELOW_BASELINE_EM * fontSizePx;
  const line = above + below + SQUAD_ROLLING_LABEL_GAP_PX;
  const baselines = labels.map((label) => label.baseline);
  labels.forEach((label, k) => {
    for (let i = 0; i < k; i += 1) {
      const other = labels[i];
      const apartX = Math.abs(label.x - other.x) - (label.width + other.width) / 2;
      const apartY = Math.abs(baselines[k] - baselines[i]) - (above + below);
      if (apartX >= SQUAD_ROLLING_LABEL_GAP_PX || apartY >= SQUAD_ROLLING_LABEL_GAP_PX) continue;
      const moved = label.value < 0 ? baselines[i] + line : baselines[i] - line;
      baselines[k] = Math.min(Math.max(moved, above), gridHeight - below);
    }
  });
  return baselines;
}

type ApexChartContext = {
  el?: Element | null;
  w?: { globals?: { gridHeight?: number; minY?: number; maxY?: number } };
};

type DrawnBar = { series: number; j: number; value: number; box: DOMRect | SVGRect };

const boxOf = (el: Element): DOMRect | SVGRect | undefined =>
  typeof (el as SVGGraphicsElement).getBBox === 'function'
    ? (el as SVGGraphicsElement).getBBox()
    : undefined;

/**
 * y of the zero line. Read off a drawn bar where there is one; otherwise from the axis
 * range, the way ApexCharts places its baseline.
 */
function zeroLineY(
  bars: DrawnBar[],
  globals: NonNullable<ApexChartContext['w']>['globals'],
): number | undefined {
  const anchored = bars.find((bar) => bar.value !== 0 && bar.box.height > 0);
  if (anchored) {
    return anchored.value > 0 ? anchored.box.y + anchored.box.height : anchored.box.y;
  }
  const { gridHeight = 0, minY, maxY } = globals || {};
  if (minY === undefined || maxY === undefined || !(maxY > minY)) return undefined;
  return Math.min(Math.max((gridHeight * maxY) / (maxY - minY), 0), gridHeight);
}

/**
 * Moves and colours the drawn labels — wire to `chart.events.mounted` and `updated`.
 * Works in SVG user units (getBBox, attributes), so the FitPreviewWidth scale on the
 * preview does not enter.
 */
export function layoutSquadRollingDataLabels(chart: ApexChartContext): void {
  const root = chart?.el;
  const globals = chart?.w?.globals;
  const gridHeight = globals?.gridHeight;
  if (!root || !gridHeight) return;

  const bars: DrawnBar[] = [];
  root.querySelectorAll('path.apexcharts-bar-area').forEach((path) => {
    const box = boxOf(path);
    if (!box) return;
    bars.push({
      series: Number(path.getAttribute('index')),
      j: Number(path.getAttribute('j')),
      value: Number(path.getAttribute('val')),
      box,
    });
  });
  const zeroY = zeroLineY(bars, globals);

  const outsideByPeriod = new Map<number, Array<SquadRollingOutsideLabel & { text: Element }>>();
  let fontSize = 0;

  root.querySelectorAll('g.apexcharts-datalabels').forEach((wrap) => {
    const seriesIndex = Number(wrap.getAttribute('data:realIndex'));
    // One label group per data point, in data order — matched by index, not by x: a bar
    // of exactly 0 is drawn as a bare "M 0 <gridHeight>" with nothing to match against.
    Array.from(wrap.children)
      .filter((group) => group.classList.contains('apexcharts-data-labels'))
      .forEach((group, j) => {
        const text = group.querySelector('text.apexcharts-datalabel');
        // Missing period: the formatter left the label empty.
        if (!text || !text.textContent) return;

        const bar = bars.find((b) => b.series === seriesIndex && b.j === j);
        const fontSizePx = parseFloat(text.getAttribute('font-size') || '');
        const flat = !!bar && (bar.value === 0 || bar.box.width === 0);
        const edges =
          !bar || !Number.isFinite(bar.value)
            ? undefined
            : flat
            ? zeroY === undefined
              ? undefined
              : { top: zeroY, bottom: zeroY }
            : { top: bar.box.y, bottom: bar.box.y + bar.box.height };
        if (!bar || !edges || !Number.isFinite(fontSizePx)) {
          // Left where ApexCharts put it, but never white on white.
          text.setAttribute('fill', SQUAD_ROLLING_LABEL_COLORS.outside);
          return;
        }

        const labelWidthPx = boxOf(text)?.width;
        const value = flat ? 0 : bar.value;
        const placement = placeSquadRollingLabel({
          value,
          barTop: edges.top,
          barBottom: edges.bottom,
          gridHeight,
          fontSizePx,
          seriesIndex,
          labelWidthPx,
          barWidthPx: flat ? undefined : bar.box.width,
        });
        text.setAttribute('y', String(placement.baseline));
        text.setAttribute('fill', placement.color);
        if (!placement.inside && labelWidthPx !== undefined) {
          fontSize = fontSizePx;
          const period = outsideByPeriod.get(j) || [];
          period.push({
            text,
            x: Number(text.getAttribute('x')),
            width: labelWidthPx,
            value,
            baseline: placement.baseline,
          });
          outsideByPeriod.set(j, period);
        }
      });
  });

  // Series are walked in order, so each period's list is brutto before netto.
  outsideByPeriod.forEach((labels) => {
    staggerSquadRollingLabels(labels, fontSize, gridHeight).forEach((baseline, k) => {
      labels[k].text.setAttribute('y', String(baseline));
    });
  });
}
